import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  appNavigationConstants,
  appNavigationConstants_Mobile,
} from "../../../utils/constants/header.constants";
import {
  MOBILE_ACCOUNTS_ROUTE,
  HOME_ROUTE,
  MOBILE_MENU_ROUTE,
  MY_PROFILE_ROUTE,
  EXCHANGE_SPORTS_ROUTE,
  CASINO_ROUTE,
  INT_CASINO_ROUTE,
  LOGIN_ROUTE,
  useAppSelector,
  setIsOneClickBettingEnabled,
  EXCHANGE_EVENT_TYPES,
  getInplayEvents,
  showToast,
  AppNavigationEnum,
} from "@hypexone/uicore";
import {
  useAppDispatch,
  setCompetition,
  setEventType,
  SPORTS_MAP,
} from "@hypexone/uicore";
import { isDesktop, isMobile } from "react-device-detect";
import React, { useState } from "react";
import HeaderSearchInput from "./HeaderSearchInput";
import { IoIosSettings } from "react-icons/io";
import { Radio, Settings } from "lucide-react";
import CheckboxComponent from "../shared/CheckboxComponent";
import CasinoIcon from "../../../assets/icons/header-icons/menu-casino.png";
import Modal from "../shared/ModalComponent";
import StakeSettings from "../user-menu-pages/StakeSettings";
import { requiresLoginForNavigation } from "../../../utils/helpers/path.helpers";
interface SubHeaderProps {
  disableSportsSubheader?: boolean;
}
// Display-only renames. `title` still keys the route, SPORTS_MAP and the live
// event counts, so the label is overridden here rather than in the constants.
const NAV_LABEL_OVERRIDES: Record<string, string> = {
  Football: "Soccer",
  Sportsbook: "Virtual Cricket",
};

const navLabel = (title: string, fallback?: string) =>
  NAV_LABEL_OVERRIDES[title] ?? fallback ?? title;

const SubHeader: React.FC<SubHeaderProps> = ({ disableSportsSubheader }) => {
  const pathName = useLocation().pathname;
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { langData } = useAppSelector((state) => state.cms);
  const { isOneClickBettingEnabled } = useAppSelector((state) => state.betSlip);

  const [isMobileSearchPopupOpen, setIsMobileSearchPopupOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const { events: catalogEvents, selectedEventType } = useAppSelector(
    (state) => state.catalog,
  );
  const { allowedConfig } = useAppSelector((state) => state.common);
  const { loggedIn } = useAppSelector((state) => state.auth);
  const inplayEvents = getInplayEvents(catalogEvents, allowedConfig);

  const handleNavLinkClick = (title: string) => {
    dispatch(setCompetition({ id: "", name: "", slug: "" }));
    const sport = SPORTS_MAP.get(title);
    if (sport) {
      dispatch(setEventType(sport));
    }
  };

  const onNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    title: string,
    navigationUrl: string,
  ) => {
    if (requiresLoginForNavigation(navigationUrl)) {
      if (!loggedIn) {
        e.preventDefault();
        navigate(LOGIN_ROUTE);
      }
      return;
    }
    handleNavLinkClick(title);
  };

  const [showStakeModal, setShowStakeModal] = useState<boolean>(false);

  const showSettingsTab =
    isDesktop &&
    /^\/exchange_sports\/[^/]+\/[^/]+\/[^/]+\/[^/]+$/.test(location.pathname);

  const excludedRoutes = [
    HOME_ROUTE,
    MOBILE_ACCOUNTS_ROUTE,
    MOBILE_MENU_ROUTE,
    MY_PROFILE_ROUTE,
    "/change-password",
  ];

  const excludeSubHeader =
    !excludedRoutes.includes(pathName) ||
    !pathName.startsWith(EXCHANGE_SPORTS_ROUTE);

  // Create a combined array of all sports with their event counts
  const allSportsWithCounts = EXCHANGE_EVENT_TYPES.map((sport) => {
    const inplayEvent = inplayEvents.find(
      (event) => event?.sportName?.toLowerCase() === sport?.name?.toLowerCase(),
    );
    return {
      ...sport,
      eventCount: inplayEvent ? inplayEvent.events.length : 0,
      sportName: sport.name,
    };
  });

  const getEventCountForTitle = (title: string): number => {
    const sportWithCount = allSportsWithCounts.find(
      (s) => s.sportName?.toLowerCase() === title?.toLowerCase(),
    );
    return sportWithCount?.eventCount ?? 0;
  };

  const isNavItemActive = (
    navigationUrl: string,
    defaultActive: boolean,
  ): boolean => {
    if (navigationUrl.includes("?")) {
      const [targetPath, targetSearch = ""] = navigationUrl.split("?");
      if (location.pathname !== targetPath) return false;

      const currentParams = new URLSearchParams(location.search);
      const targetParams = new URLSearchParams(targetSearch);
      for (const [key, value] of targetParams.entries()) {
        if (currentParams.get(key) !== value) return false;
      }
      return true;
    }

    const subProvider = new URLSearchParams(location.search)
      .get("subProvider")
      ?.toUpperCase();
    if (subProvider === "FANTASY11" || subProvider === "RANDORA") {
      return false;
    }

    return defaultActive;
  };

  const sportsBookSplitIndex = appNavigationConstants.findIndex(
    (item) => item.title === AppNavigationEnum.SPORTS_BOOK,
  );
  const desktopFixedNavItems =
    sportsBookSplitIndex >= 0
      ? appNavigationConstants.slice(0, sportsBookSplitIndex + 1)
      : appNavigationConstants;
  const desktopScrollableNavItems =
    sportsBookSplitIndex >= 0
      ? appNavigationConstants.slice(sportsBookSplitIndex + 1)
      : [];

  const renderDesktopNavItem = ({
    title,
    navigationUrl,
    langKey,
    showNewTag,
  }: (typeof appNavigationConstants)[number]) => {
    const eventCount = getEventCountForTitle(title);

    return (
      <div
        key={title}
        className="relative inline-flex shrink-0 items-center"
      >
        <NavLink
          to={navigationUrl}
          className={({ isActive: defaultActive }) => {
            const isActive = isNavItemActive(navigationUrl, defaultActive);

            return `text-center text-xs text-nowrap px-3 py-[7px]
                        border-r border-gray4 capitalize
                        ${
                          title === "Casino" || title === "Int Casino"
                            ? "bg-header-gradient text-white"
                            : ""
                        }
                        ${
                          isActive
                            ? "bg-[#ffdc7a] shadow-[inset_0_0px_5px_0_rgba(83,33,33,.5)]"
                            : ""
                        }`;
          }}
          onClick={(e) => onNavClick(e, title, navigationUrl)}
        >
          <div className="flex items-center justify-center gap-1">
            {navLabel(title, langData?.[langKey])}
            {eventCount > 0 && (
              <span className="inline-flex items-center rounded bg-white text-[10px] font-normal text-red-500 absolute top-[-10px] left-1/2 z-20 -translate-x-1/2">
                <div className="px-1">
                  <Radio className="h-3 w-3 shrink-0 text-red-500" />
                </div>
                <div className="bg-red-500 text-white font-bold rounded-r px-1">
                  {eventCount}
                </div>
              </span>
            )}
            {title === "Casino" && (
              <img
                src={CasinoIcon}
                alt="casino-icon"
                className="h-[18px] w-[18px]"
              />
            )}
          </div>
        </NavLink>
        {showNewTag && (
          <span className="new-tag-badge !top-0 !right-0">
            {langData?.["new"] || "NEW"}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-w-full font-bold text-[12px] relative overflow-visible">
      {isMobile &&
        excludeSubHeader &&
        (pathName == `${HOME_ROUTE}` ? true : disableSportsSubheader) && (
          <div className="flex bg-white justify-center w-full gap-[1px]">
            <div className="flex items-center w-full justify-center bg-black overflow-auto hide-scrollbar">
              <div className="flex">
                {appNavigationConstants_Mobile.map(
                  ({ title, navigationUrl }, index) => {
                    const eventCount = getEventCountForTitle(title);
                    return (
                      <NavLink
                        to={navigationUrl}
                        key={title}
                        className={({ isActive }) =>
                          `flex justify-center font-[400] items-center gap-1 px-[10px] py-[7px] text-white text-[14px] text-nowrap 
          ${
            index !== appNavigationConstants_Mobile.length - 1
              ? "border-r-[1px] border-gray4"
              : ""
          }
          ${
            isActive
              ? "bg-black1 border-x border-t border-white rounded-t-[8px]"
              : ""
          }`
                        }
                        onClick={(e) => onNavClick(e, title, navigationUrl)}
                      >
                        {navLabel(title)}
                        {eventCount > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded py-[2px] pl-1 pr-1.5 text-[10px] font-normal text-white"
                            style={{
                              background:
                                "linear-gradient(-180deg, #F72424 0%, #BB1C00 100%)",
                            }}
                          >
                            <Radio className="h-3 w-3 shrink-0" />
                            {eventCount}
                          </span>
                        )}
                      </NavLink>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        )}
      {isMobileSearchPopupOpen && (
        <div className="absolute bg-white p-2 top-0 w-full">
          <HeaderSearchInput
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setIsMobileSearchPopupOpen={setIsMobileSearchPopupOpen}
          />
        </div>
      )}

      {isDesktop && (
        <div className="relative flex w-full min-w-0 overflow-visible">
          <div className="flex shrink-0">
            {desktopFixedNavItems.map(renderDesktopNavItem)}
          </div>
          {desktopScrollableNavItems.length > 0 && (
            <div className="flex min-w-0 flex-1 overflow-visible pr-[280px]">
              <div className="flex min-w-0 overflow-x-auto hide-scrollbar pt-3 -mt-3">
                {desktopScrollableNavItems.map(renderDesktopNavItem)}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="absolute right-0 flex items-center gap-2 bg-sub-header-gradient h-full">
        <div className="text-xs flex flex-col leading-tight pl-1">
          <span className="font-normal text-gray-700">Time Zone:</span>
          <span className="font-semibold text-black">
            {(() => {
              try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const formatter = new Intl.DateTimeFormat("en-US", {
                  timeZone: tz,
                  timeZoneName: "short",
                });
                const parts = formatter.formatToParts(new Date());
                const tzName =
                  parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
                return tzName;
              } catch {
                return "—";
              }
            })()}
          </span>
        </div>
        <div className="bg-header-gradient h-full flex items-center justify-center text-[#ffb600]">
          <div className="relative">
            <div className="h-1 w-full bg-[#ffb600] absolute top-[-11px]"></div>
            <div className="px-4">
              <CheckboxComponent
                checked={isOneClickBettingEnabled}
                onChange={() => {
                  if (loggedIn) {
                    dispatch(
                      setIsOneClickBettingEnabled(!isOneClickBettingEnabled),
                    );
                  } else {
                    dispatch(
                      showToast({
                        type: "error",
                        message: "Please login to enable one click betting",
                      }),
                    );
                  }
                }}
                label={"One Click Bet"}
                className={`w-4 h-4 rounded-md font-medium ${
                  isOneClickBettingEnabled
                    ? "opacity-100 accent-[#ffb600]"
                    : "opacity-30"
                }`}
              />
            </div>
          </div>
        </div>
        <div
          className="flex items-center gap-1 cursor-pointer"
          onClick={() =>
            loggedIn
              ? setShowStakeModal(true)
              : dispatch(
                  showToast({
                    type: "error",
                    message: "Please login to edit stake",
                  }),
                )
          }
        >
          Setting <Settings className="w-4 h-4" />
        </div>
      </div>
      {showStakeModal && (
        <Modal
          isOpen={showStakeModal}
          onClose={() => setShowStakeModal(false)}
          title={langData?.["edit_stake"] ?? "Edit Stake"}
          isContentWidth
        >
          <StakeSettings />
        </Modal>
      )}
    </div>
  );
};

export default SubHeader;
